import { ZuruflexApiError } from '@zuruflex/api-client';
import {
  FUEL_TYPES,
  TRANSMISSIONS,
  VEHICLE_CLASSES,
  minorUnitsPerMajor,
  type DriverPolicy,
  type FuelType,
  type Transmission,
  type Vehicle,
  type VehicleClass,
} from '@zuruflex/types';
import { Alert, Button, Combobox, FormField, Input, Textarea } from '@zuruflex/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '../lib/api.js';
import { usePartner } from '../lib/auth.js';
import { Choice, DriverFields } from './car-fields.js';

const THIS_YEAR = new Date().getFullYear();

/**
 * Correcting a car that already exists.
 *
 * Until this existed a car was whatever it was typed as the first time: a
 * transposed plate, a rate set in the wrong currency and a 2019 entered as
 * 2009 were all permanent, and the only way out was a second listing and a
 * support conversation. The API has taken a PATCH all along.
 */
export function CarDetailsForm({ vehicle }: { vehicle: Vehicle }) {
  const { t } = useTranslation(['partner', 'common']);
  const { partner } = usePartner();
  const queryClient = useQueryClient();

  /** Rates are held in minor units and typed in major ones. */
  const toMajor = (amount: number, currency: string) =>
    String(amount / minorUnitsPerMajor(currency));

  const [make, setMake] = useState(vehicle.make);
  const [model, setModel] = useState(vehicle.model);
  const [year, setYear] = useState(String(vehicle.year));
  const [plateNumber, setPlateNumber] = useState(vehicle.plateNumber);
  const [vehicleClass, setVehicleClass] = useState<VehicleClass>(vehicle.vehicleClass);
  const [transmission, setTransmission] = useState<Transmission>(vehicle.transmission);
  const [fuelType, setFuelType] = useState<FuelType>(vehicle.fuelType);
  const [seats, setSeats] = useState(String(vehicle.seats));
  const [doors, setDoors] = useState(String(vehicle.doors));
  const [locationId, setLocationId] = useState(vehicle.locationId);
  const [dailyRate, setDailyRate] = useState(toMajor(vehicle.dailyRate, vehicle.currency));
  const [driverPolicy, setDriverPolicy] = useState<DriverPolicy>(vehicle.driverPolicy);
  const [driverRate, setDriverRate] = useState(
    vehicle.driverDailyRate ? toMajor(vehicle.driverDailyRate, vehicle.currency) : '',
  );
  const [description, setDescription] = useState(vehicle.description ?? '');
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  const { data: locations } = useQuery({
    queryKey: ['locations'],
    queryFn: () => api.listLocations(),
  });

  // The branch decides the currency, so moving a car between them re-prices
  // it — and the rate field has to say which currency it is being read as
  // before the number is sent.
  const chosen = locations?.find((location) => location.id === locationId);
  const currency = chosen?.currency ?? vehicle.currency;
  const locationOptions = (locations ?? []).map((location) => ({
    value: location.id,
    label: `${location.name} — ${location.city}`,
  }));

  const save = useMutation({
    mutationFn: () =>
      api.updateVehicle(vehicle.id, {
        make: make.trim(),
        model: model.trim(),
        year: Number(year),
        plateNumber: plateNumber.trim().toUpperCase(),
        vehicleClass,
        transmission,
        fuelType,
        seats: Number(seats),
        doors: Number(doors),
        locationId,
        dailyRate: Math.round(Number(dailyRate) * minorUnitsPerMajor(currency)),
        driverPolicy,
        ...(driverPolicy === 'SELF_DRIVE_ONLY'
          ? {}
          : { driverDailyRate: Math.round(Number(driverRate) * minorUnitsPerMajor(currency)) }),
        description: description.trim() || undefined,
      }),
    onMutate: () => {
      setError(null);
      setSaved(false);
    },
    onError: (cause) =>
      setError(cause instanceof ZuruflexApiError ? cause.message : t('common:error.unexpected')),
    onSuccess: async () => {
      setSaved(true);
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['vehicle', vehicle.id] }),
        queryClient.invalidateQueries({ queryKey: ['my-fleet'] }),
      ]);
    },
  });

  const driverRateNeeded = driverPolicy !== 'SELF_DRIVE_ONLY' && !(Number(driverRate) > 0);
  const ready = Boolean(
    make && model && plateNumber && locationId && Number(dailyRate) > 0 && !driverRateNeeded,
  );

  return (
    <form
      className="flex flex-col gap-4"
      onSubmit={(event: FormEvent) => {
        event.preventDefault();
        save.mutate();
      }}
    >
      {/*
        Said before they edit, not after the listing has gone. The API sends
        a published car back for review when anything a customer sees
        changes, which is right — but a partner who finds that out by
        watching their car leave the site will not touch this form again.
      */}
      {vehicle.listingStatus === 'PUBLISHED' ? (
        <Alert variant="info">{t('carDetail.editGoesBackToReview')}</Alert>
      ) : null}

      {error ? <Alert variant="danger">{error}</Alert> : null}
      {saved && !save.isPending ? <Alert variant="success">{t('carDetail.saved')}</Alert> : null}

      <div className="grid gap-3 sm:grid-cols-2">
        <FormField label={t('addCar.make')} required>
          {(field) => (
            <Input
              {...field}
              value={make}
              required
              onChange={(event) => setMake(event.target.value)}
            />
          )}
        </FormField>
        <FormField label={t('addCar.model')} required>
          {(field) => (
            <Input
              {...field}
              value={model}
              required
              onChange={(event) => setModel(event.target.value)}
            />
          )}
        </FormField>
      </div>

      <div className="grid gap-3 sm:grid-cols-2">
        <FormField label={t('addCar.year')} required>
          {(field) => (
            <Input
              {...field}
              type="number"
              min={1980}
              max={THIS_YEAR + 1}
              value={year}
              required
              onChange={(event) => setYear(event.target.value)}
            />
          )}
        </FormField>
        <FormField label={t('common:field.plate')} required>
          {(field) => (
            <Input
              {...field}
              value={plateNumber}
              required
              className="font-mono uppercase"
              onChange={(event) => setPlateNumber(event.target.value)}
            />
          )}
        </FormField>
      </div>

      <Choice
        label={t('common:field.class')}
        options={VEHICLE_CLASSES}
        value={vehicleClass}
        onChange={setVehicleClass}
        render={(option) => t(`common:vehicleClass.${option}`)}
      />

      <div className="grid gap-3 sm:grid-cols-2">
        <Choice
          label={t('common:field.transmission')}
          options={TRANSMISSIONS}
          value={transmission}
          onChange={setTransmission}
          render={(option) => t(`common:transmission.${option}`)}
        />
        <Choice
          label={t('common:field.fuel')}
          options={FUEL_TYPES}
          value={fuelType}
          onChange={setFuelType}
          render={(option) => t(`common:fuelType.${option}`)}
        />
      </div>

      <div className="grid gap-3 sm:grid-cols-2">
        <FormField label={t('common:field.seats')} required>
          {(field) => (
            <Input
              {...field}
              type="number"
              min={1}
              max={60}
              value={seats}
              required
              onChange={(event) => setSeats(event.target.value)}
            />
          )}
        </FormField>
        <FormField label={t('common:field.doors')} required>
          {(field) => (
            <Input
              {...field}
              type="number"
              min={1}
              max={8}
              value={doors}
              required
              onChange={(event) => setDoors(event.target.value)}
            />
          )}
        </FormField>
      </div>

      <FormField label={t('addCar.pickUpPoint')} hint={t('addCar.pickUpHint')} required>
        {(field) => (
          <Combobox
            id={field.id}
            options={locationOptions}
            value={locationId}
            onChange={setLocationId}
            placeholder={t('addCar.choosePickUp')}
            searchPlaceholder={t('addCar.searchPickUp')}
            emptyMessage={t('addCar.noPickUp')}
          />
        )}
      </FormField>

      {/* Moving a car to a branch that prices in another currency does not
          convert the number in the box — it re-reads it. Worth saying. */}
      {currency === vehicle.currency ? null : (
        <Alert variant="warning">
          {t('carDetail.currencyChanges', { from: vehicle.currency, to: currency })}
        </Alert>
      )}

      <DriverFields
        available={partner.providesDrivers}
        currency={currency}
        policy={driverPolicy}
        onPolicyChange={setDriverPolicy}
        rate={driverRate}
        onRateChange={setDriverRate}
      />

      <FormField label={t('addCar.dailyRate', { currency })} hint={t('addCar.rateHint')} required>
        {(field) => (
          <Input
            {...field}
            type="number"
            min={1}
            step="1"
            value={dailyRate}
            required
            onChange={(event) => setDailyRate(event.target.value)}
          />
        )}
      </FormField>

      <FormField label={t('addCar.description')}>
        {(field) => (
          <Textarea
            {...field}
            rows={3}
            value={description}
            onChange={(event) => setDescription(event.target.value)}
          />
        )}
      </FormField>

      <div className="flex justify-end">
        <Button type="submit" disabled={!ready} isLoading={save.isPending}>
          {t('common:action.save')}
        </Button>
      </div>
    </form>
  );
}
